import React, { useState, useEffect } from "react";
import { makeStyles } from '@material-ui/core/styles';
import { FormHelperText } from '@material-ui/core';

import I18n from "../../../../base/i18n.js.jsx";

import HtmlContent from "../../../../base/components/html_content.js";
import TextField from "../../../../base/inputs/text_field.js";

import PaymentMethodFields from "../../../../base/user_fields/payment_method_fields.js";

import MoneyFormatter from "../../../../utils/money_formatter.js";

const blockStyles = makeStyles(theme => ({
  root: {
  },
  disclaimer: {
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: theme.typography.fontWeightLight,
    color: theme.palette.grayText,
    marginBottom: "20px",
    ['@media (max-width: 768px)']: {
      marginTop: "5px",
      fontSize: "12px",
      lineHeight: "18px",
      marginBottom: "17px",
    },
  },
  moneyContainer: {
    marginBottom: "30px",
    ['@media (max-width: 768px)']: {
      marginBottom: "20px",
    },
  },
  moneyContainerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "start",
    fontSize: "28px",
    height: "55px",
    "& div + div": {
      marginLeft: "20px",
      flexGrow: "0",
      ['@media (max-width: 768px)']: {
        marginLeft: "12px",
      },
    },
    ['@media (max-width: 768px)']: {
      height: "44px",
      fontSize: "20px",
    },
  },
  nominal: {

  },
  multiplier: {
    fontSize: "24px",
    textTransform: "uppercase",
    ['@media (max-width: 768px)']: {
      fontSize: "16px",
    },
  },
  count: {
    flexBasis: "160px",
    flexGrow: "1",
    flexShrink: "0",
    display: "flex",
    height: "48px",
    ['@media (max-width: 768px)']: {
      flexBasis: "75px",
      height: "33px",
    },
  },
  countInput: {
    height: "100%",
    "& .MuiInputBase-root": {
      height: "100%"
    },
    "& input": {
      height: "100%",
      padding: "8px 20px",
      fontSize: "28px",
      ['@media (max-width: 768px)']: {
        padding: "8px 12px",
        fontSize: "20px",
      },
    }
  },
  equal: {

  },
  result: {

  },
  paymentMethodTitle: {
    fontSize: "20px",
    lineHeight: "24px",
    marginBottom: "34px",
    ['@media (max-width: 768px)']: {
      fontWeight: theme.typography.fontWeightRegular,
      fontSize: "14px",
      marginBottom: "5px",
    },
  },
  paymentMethodSubtitle: {
    fontWeight: theme.typography.fontWeightLight,
    fontSize: "16px",
    lineHeight: "18px",
    marginBottom: "20px",
    ['@media (max-width: 768px)']: {
      fontSize: "12px",
      marginBottom: "5px",
    },
  },
  paymentMethodContainer: {
    marginTop: "40px",
    marginBottom: "50px",
    ['@media (max-width: 768px)']: {
      marginTop: "24px",
      marginBottom: "26px",
    },
  },
}));

const PaymentMethodSection = (props) => {

  const moneyFormat = (value) => {
    return MoneyFormatter.format(value, "0,0[,][00]");
  };

  const classes = blockStyles();

  const { minInvestmentAmount, nominalInvestmentAmount } = props;
  const { onChangeStepsCount, onChangePaymentMethod } = props;

  const [count, setCount] = useState(props.stepsCount);
  const [amount, setAmount] = useState(props.amount);
  const [limitForInvestmentAmount, setLimitForInvestmentAmount] = useState(props.limitForInvestmentAmount)

  const [paymentMethod, setPaymentMethod] = useState({...props.paymentMethod});

  const [amountErrors, setAmountErrors] = useState(props.amountErrors);
  const [paymentMethodErrors, setPaymentMethodErrors] = useState(props.paymentMethodErrors);

  useEffect(() => {
    setAmount(props.amount);
  }, [props.amount]);

  useEffect(() => {
    setLimitForInvestmentAmount(props.limitForInvestmentAmount);
  }, [props.limitForInvestmentAmount]);

  useEffect(() => {
    setAmountErrors(props.amountErrors)
  }, [props.amountErrors]);

  useEffect(() => {
    setPaymentMethodErrors(props.paymentMethodErrors)
  }, [props.paymentMethodErrors])

  useEffect(() => {
    updateTotalAmountMessages();
  }, [amount, limitForInvestmentAmount]);

  const updateTotalAmountMessages = () => {
    if(!amount) {
      setAmountErrors([I18n.t("errors.projects/participant.fields.desired_investment_amount.blank")]);
    }
    else {
      const { title } = props
      if(amount > limitForInvestmentAmount && title != "gmbh" && title != "cokg") {
        setAmountErrors([I18n.t("errors.projects/participant.fields.desired_investment_amount.less_than_or_equal_to", {amount: limitForInvestmentAmount})]);
      }
      else if(amount < minInvestmentAmount) {
        setAmountErrors([I18n.t("errors.projects/participant.fields.desired_investment_amount.greater_than_or_equal_to", {amount: minInvestmentAmount})]);
      }
      else {
        setAmountErrors(undefined)
      }
    }
  }

  const onChangeCount = (value) => {
    setCount(value);
    onChangeStepsCount(parseInt(value));
  }

  const onChangeMethod = (method) => {
    setPaymentMethod(method)
    if(onChangePaymentMethod) {
      onChangePaymentMethod(method)
    }
  }

  return (
    <div className = { classes.root } >
      <HtmlContent className = { classes.disclaimer } >
        { I18n.t("web.modal.submit_investment.payment_method.disclaimer", {
          maximum: moneyFormat(limitForInvestmentAmount),
          minimum: moneyFormat(minInvestmentAmount),
          currency: I18n.t("currency.euro")
        }) }
      </HtmlContent>
      <div className = { classes.moneyContainer } >
        <div className = { classes.moneyContainerRow } >
          <div className = { classes.nominal } >
            { moneyFormat(nominalInvestmentAmount) } 
            &nbsp; 
            { I18n.t("currency.euro") }
          </div>
          <div className = { classes.multiplier } > { "x" } </div>
          <div className = { classes.count } >
            <TextField type = { "number" }
                       value = { count } 
                       onChange = { onChangeCount } 
                       className = { classes.countInput } 
                       hasError = { Boolean(amountErrors && amountErrors.length > 0) }
                       propsPrior = { true } />

          </div>
          <div className = { classes.equal } > { "=" } </div>
          <div className = { classes.result } >
            { moneyFormat(amount) }
            &nbsp; 
            { I18n.t("currency.euro") }
          </div>
        </div>
        {
          amountErrors && amountErrors.length > 0 &&
          <FormHelperText error = { true }>
            { amountErrors.join(", ") }
          </FormHelperText>
        }
      </div>
      <div className = { classes.paymentMethodTitle } >
        { I18n.t("shared.payment_method", {count: 1}) }
      </div>
      <div className = { classes.paymentMethodSubtitle } >
        { I18n.t("shared.transfer_prepayment") }
      </div>
      <HtmlContent className = { classes.disclaimer } >
        { I18n.t("web.modal.submit_investment.payment_method.payment_method_disclaimer") }
      </HtmlContent>
      <PaymentMethodFields paymentMethod = { paymentMethod }
                           errorData = { paymentMethodErrors } 
                           onChange = { onChangeMethod } 
                           showDisclaimer = { false }
                           className = { classes.paymentMethodContainer } />
    </div>
  );
}

export default PaymentMethodSection;